import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { KDK_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { useGetKdkAprQuery } from 'hooks/backend/kdk'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useDividends, useStaking } from 'hooks/useStaking'
import { useGetTokenPrice, useGetTokenPrices } from 'hooks/useTokenPrice'
import { useCallback, useMemo } from 'react'
import { Token as PortfolioToken } from 'types/portfolio'
import { Address } from 'viem'

export interface XKdkStakingPosition {
  xkdkToken: Token | null | undefined
  xkdkBalance: {
    allocated: CurrencyAmount<any> | undefined
    redeeming: CurrencyAmount<any> | undefined
    total: CurrencyAmount<any> | undefined
  }
  xkdkBalanceUSD: {
    allocated: number
    redeeming: number
    total: number
  }
  pendingRewards: Array<{
    token: PortfolioToken
    amount: CurrencyAmount<any>
    amountUSD: number
  }>
  totalPendingRewardsUSD: number
  currentApr: number | undefined
  totalValueUSD: number
  hasPosition: boolean
  refreshRewards?: () => Promise<void>
  refresh?: () => Promise<void>
}

export function useXKdkStakingPosition(options?: { enabled?: boolean }): {
  data: XKdkStakingPosition | undefined
  isLoading: boolean
} {
  const { isConnected, chainId } = useConnection()
  const enabled = isConnected && options?.enabled !== false

  const xkdkAddress = XKDK_ADDRESSES[chainId]
  const kdkAddress = KDK_ADDRESSES[chainId]
  const xkdkToken = useToken(xkdkAddress)

  const stakingData = useStaking({ refetchPolicy: PORTFOLIO_REFETCH_POLICY })
  const dividendsData = useDividends({ refetchPolicy: PORTFOLIO_REFETCH_POLICY })

  const aprQueryParams = useMemo(() => {
    const endTime = Math.floor(Date.now() / 1000)
    const startTime = endTime - 24 * 60 * 60 // 24 hours ago
    return {
      resolution: '1h' as const,
      startTime,
      endTime,
    }
  }, [])

  const { data: aprData } = useGetKdkAprQuery(aprQueryParams, {
    enabled,
  })

  const { price: kdkPrice } = useGetTokenPrice(kdkAddress, {
    enabled,
  })

  const rewardTokenAddresses = useMemo(() => {
    const addresses: string[] = []
    if (dividendsData.distributedTokens) {
      dividendsData.distributedTokens.forEach((token) => {
        if (token?.address) {
          addresses.push(token.address)
        }
      })
    }
    return addresses
  }, [dividendsData.distributedTokens])

  const { prices: rewardTokenPricesArray } = useGetTokenPrices(rewardTokenAddresses)

  const refreshRewards = useCallback(async () => {
    await Promise.allSettled([dividendsData.refresh?.()])
  }, [dividendsData])

  const refreshPosition = useCallback(async () => {
    await Promise.allSettled([stakingData.refresh?.(), dividendsData.refresh?.()])
  }, [stakingData, dividendsData])

  const stakingPosition = useMemo<XKdkStakingPosition | undefined>(() => {
    if (!enabled) {
      return undefined
    }

    if (!xkdkToken || !stakingData.allocated || !stakingData.redeeming) {
      return undefined
    }

    const allocated = stakingData.allocated
    const redeeming = stakingData.redeeming

    const allocatedValue = allocated ? parseFloat(allocated.toExact()) : 0
    const redeemingValue = redeeming ? parseFloat(redeeming.toExact()) : 0
    // Total only includes allocated (actively staked), not redeeming (unstaking)
    const totalXkdkBalance = allocatedValue

    const xkdkPrice = kdkPrice || 0

    const allocatedUSD = allocatedValue * xkdkPrice
    const redeemingUSD = redeemingValue * xkdkPrice
    const totalXkdkUSD = totalXkdkBalance * xkdkPrice

    const total = allocated

    const pendingRewards: Array<{
      token: PortfolioToken
      amount: CurrencyAmount<any>
      amountUSD: number
    }> = []

    let totalPendingRewardsUSD = 0

    if (dividendsData.pendingRewardsAmount && dividendsData.distributedTokens) {
      dividendsData.pendingRewardsAmount.forEach((amount, index) => {
        if (!amount) return

        const token = dividendsData.distributedTokens?.[index]
        if (!token) return

        const amountValue = parseFloat(amount.toExact())
        if (amountValue <= 0) return

        const tokenPrice = (rewardTokenPricesArray && rewardTokenPricesArray[index]) || 0
        const amountUSD = amountValue * tokenPrice

        pendingRewards.push({
          token: {
            id: token.address as Address,
            address: token.address as Address,
            name: token.name || '',
            symbol: token.symbol || '',
            decimals: token.decimals,
            logoUri: (token as any).logoURI || undefined,
          },
          amount,
          amountUSD,
        })

        totalPendingRewardsUSD += amountUSD
      })
    }

    const totalValueUSD = allocatedUSD

    const hasPosition = allocatedValue > 0 || redeemingValue > 0

    return {
      xkdkToken,
      xkdkBalance: {
        allocated,
        redeeming,
        total,
      },
      xkdkBalanceUSD: {
        allocated: allocatedUSD,
        redeeming: redeemingUSD,
        total: totalXkdkUSD,
      },
      pendingRewards,
      totalPendingRewardsUSD,
      currentApr: aprData?.currentApr,
      totalValueUSD,
      hasPosition,
      refreshRewards,
      refresh: refreshPosition,
    }
  }, [
    enabled,
    xkdkToken,
    kdkPrice,
    stakingData.allocated,
    stakingData.redeeming,
    dividendsData.pendingRewardsAmount,
    dividendsData.distributedTokens,
    rewardTokenPricesArray,
    aprData?.currentApr,
    refreshRewards,
    refreshPosition,
  ])

  const isLoading = enabled && !stakingPosition

  return {
    data: stakingPosition,
    isLoading,
  }
}
